import React, { useCallback, useRef, useMemo } from 'react';
import { Table, Tag, Button } from '@umu/umu-design';
import { lang } from '@umu/ukit-lang';
import './index.scss';
import { fetchPracticeInviteUser, fetchPracticeInviteScore } from './request';
import { useInfiniteScroll } from 'ahooks';
import { Loading } from 'common/src/components';
import { UserAvatar } from 'businessCommon/src/components';
import { INVITE_SCORE_STATUS_MAP } from './constants';
import {
    EInviteScoreStatus,
    PRACTICE_TYPE,
    IPracticeInviteUserList,
    IPracticeInviteUserInfo,
    EPracticeInviteStatus,
} from './types';
import { Toast } from 'common/src/services';
import { $window } from '@umu/umu-dom';
import ThemeConfigProvider from 'common/src/config/theme/themeConfig';
import { ThemeConfig } from '@umu/design-base';
import { THEME_TYPE } from 'common/src/config/theme/themeConfigConst';
import { If } from 'common/src/components';

interface IInviteEvaluationListProps {
    onCancel?: () => void;
    onOk?: () => void;
    objId: string;
    objType: PRACTICE_TYPE;
    seId?: string;
    practiceId: string;
    parentId: string;
    onInviteUserNumChange?: (num: number) => void;
    isShowScore?: boolean;
}

const userThemeConfig = {
    type: THEME_TYPE.DEFAULT,
    token: {
        colorPrimary: ThemeConfig.token!.colorPrimary!,
        colorText: ThemeConfig.token!.colorText,
    },
};

export const InviteEvaluationList = React.memo((props: IInviteEvaluationListProps) => {
    const {
        objId,
        objType,
        seId,
        practiceId,
        parentId,
        onInviteUserNumChange = () => void 0,
        isShowScore = true,
    } = props;
    const inviteNum = useRef(0);

    async function getUserList(params: {
        current: number;
        pageSize: number;
    }): Promise<{ list: IPracticeInviteUserList[] }> {
        return fetchPracticeInviteUser({
            channel_id: parentId,
            obj_id: objId,
            obj_type: objType,
            ...(seId ? { se_id: seId } : {}),
            practice_id: practiceId,
            page: params.current,
            size: params.pageSize,
        }).then((res) => {
            onInviteUserNumChange(res?.invite_number || 0);
            inviteNum.current = res?.invite_number || 0;
            return {
                pageInfo: res?.page_info,
                current: res?.page_info?.current_page,
                list: res?.list,
            };
        });
    }

    const { data, loading, loadingMore, noMore, mutate } = useInfiniteScroll(
        (d) =>
            getUserList({
                current: d ? d?.pageInfo?.current_page + 1 : 1,
                pageSize: d?.pageInfo.page_size || 10,
            }),
        {
            target: () =>
                $window.document.querySelector(
                    '.invite-evaluation-List .umu-design-comp-table .umu-design-comp-table-container > .umu-design-comp-table-body',
                ),
            isNoMore: (d) => d?.pageInfo?.current_page >= d?.pageInfo?.total_page_num,
        },
    );

    const updateInviteStatus = useCallback(
        (record: IPracticeInviteUserList, status: EPracticeInviteStatus) => {
            // 只有 未邀请 和 已邀请 的状态才能更新
            if (
                record.invite_status !== EInviteScoreStatus.NOT_INVITED &&
                record.invite_status !== EInviteScoreStatus.INVITED
            ) {
                return;
            }

            const isAddInvite =
                record.invite_status === EInviteScoreStatus.NOT_INVITED && status === EPracticeInviteStatus.INVITE;
            const isCancelInvite =
                record.invite_status === EInviteScoreStatus.INVITED && status === EPracticeInviteStatus.CANCEL_INVITE;
            inviteNum.current = isAddInvite
                ? inviteNum.current + 1
                : isCancelInvite
                ? inviteNum.current - 1
                : inviteNum.current;
            onInviteUserNumChange(inviteNum.current);

            const list =
                data?.list.map((item) => {
                    if (item.user_info.umu_id === record.user_info.umu_id) {
                        return {
                            ...item,
                            invite_status:
                                status === EPracticeInviteStatus.INVITE
                                    ? EInviteScoreStatus.INVITED
                                    : EInviteScoreStatus.NOT_INVITED,
                        };
                    }
                    return item;
                }) || [];

            mutate({
                ...data,
                list,
            });
        },
        [data],
    );

    const handleToInviteComment = (record: IPracticeInviteUserList, status: EPracticeInviteStatus) => {
        fetchPracticeInviteScore({
            channel_id: parentId,
            obj_id: objId,
            obj_type: objType,
            umu_ids: record?.user_info?.umu_id || '',
            invite_status: status,
            practice_id: practiceId,
        })
            .then(() => {
                if (EPracticeInviteStatus.INVITE === status) {
                    Toast.show(
                        record.invite_status === EInviteScoreStatus.INVITED
                            ? lang('已再次发送邀请通知')
                            : lang('邀请点评成功'),
                    );
                } else {
                    Toast.show(lang('撤回点评邀请成功'));
                }
                updateInviteStatus(record, status);
            })
            .catch((err) => {
                console.error('handleToInviteComment:err', err);
            });
    };

    const columns = useMemo(
        () => [
            {
                title: lang('邀请对象'),
                dataIndex: 'user_info',
                key: 'user_info',
                width: 320,
                render: (userInfo: IPracticeInviteUserInfo, record: IPracticeInviteUserList) => {
                    const { is_manager } = record || {};
                    return (
                        <div className="invite-evaluation-list-user-info">
                            <div className="invite-evaluation-list-user-info__left">
                                <UserAvatar src={userInfo.avatar} className="" width={36} />
                            </div>
                            <div className="invite-evaluation-list-user-info__right">
                                <div className="department-name-group">
                                    {userInfo.department_names.map((name) => {
                                        return (
                                            <div className="department-name_item" key={name}>
                                                {name}
                                            </div>
                                        );
                                    })}
                                </div>
                                <div className="user-info">
                                    {+is_manager === 1 ? (
                                        <ThemeConfigProvider userThemeConfig={userThemeConfig}>
                                            <div className="user-tag">
                                                <Tag color="black">{lang('经理')}</Tag>
                                            </div>
                                        </ThemeConfigProvider>
                                    ) : null}
                                    <div className="user-name">{userInfo.name}</div>
                                    <div className="user-email">{userInfo.email}</div>
                                </div>
                            </div>
                        </div>
                    );
                },
            },
            {
                title: lang('邀请状态'),
                dataIndex: 'invite_status',
                key: 'invite_status',
                width: 100,
                render: (scoreStatus: EPracticeInviteStatus) => {
                    return <div className="score-status">{INVITE_SCORE_STATUS_MAP[scoreStatus]}</div>;
                },
            },
            ...(isShowScore
                ? [
                      {
                          title: lang('评分'),
                          dataIndex: 'score',
                          key: 'score',
                          width: 100,
                          align: 'right',
                          render: (score: number, record: IPracticeInviteUserList) => {
                              const scoreStr =
                                  record.invite_status === EInviteScoreStatus.REVIEWED ? score : score || '-';
                              return <div className="score">{scoreStr}</div>;
                          },
                      },
                  ]
                : []),
            {
                title: lang('操作'),
                dataIndex: 'operation',
                key: 'operation',
                width: 120,
                render: (_: any, record: IPracticeInviteUserList) => {
                    return (
                        <div className="invite-evaluation-list-operation">
                            {isShowScore ? (
                                <>
                                    <Button
                                        type="text"
                                        disabled={record?.invite_status === EInviteScoreStatus.REVIEWED}
                                        onClick={() => handleToInviteComment(record, EPracticeInviteStatus.INVITE)}
                                    >
                                        {lang('邀请')}
                                    </Button>
                                    <Button
                                        disabled={
                                            record?.invite_status === EInviteScoreStatus.REVIEWED ||
                                            record?.invite_status === EInviteScoreStatus.NOT_INVITED
                                        }
                                        type="text"
                                        onClick={() =>
                                            handleToInviteComment(record, EPracticeInviteStatus.CANCEL_INVITE)
                                        }
                                    >
                                        {lang('撤回')}
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <If condition={record?.invite_status === EInviteScoreStatus.NOT_INVITED}>
                                        <Button
                                            type="text"
                                            onClick={() => handleToInviteComment(record, EPracticeInviteStatus.INVITE)}
                                        >
                                            {lang('邀请')}
                                        </Button>
                                    </If>
                                    <If condition={record?.invite_status === EInviteScoreStatus.INVITED}>
                                        <Button
                                            type="text"
                                            onClick={() =>
                                                handleToInviteComment(record, EPracticeInviteStatus.CANCEL_INVITE)
                                            }
                                        >
                                            {lang('撤回')}
                                        </Button>
                                    </If>
                                </>
                            )}
                        </div>
                    );
                },
            },
        ],
        [data, updateInviteStatus, isShowScore],
    );

    const TableFooter = useCallback(() => {
        // if (noMore) {
        //     return <div className="end-text">{lang('没有更多数据了')}</div>;
        // }

        if (loadingMore) {
            return <Loading className="table-footer-loading" />;
        }

        return null;
    }, [loadingMore, noMore]);

    const renderTableFooter = () => (
        <div className="table-footer">
            <TableFooter />
        </div>
    );

    return (
        <div className="invite-evaluation-List">
            {loading ? (
                <Loading />
            ) : (
                <Table
                    scroll={{
                        y: '3.5rem',
                        x: '100%',
                    }}
                    columns={columns}
                    data={data?.list}
                    pagination={false}
                    tableLayout="fixed"
                    footer={renderTableFooter}
                    emptyText={lang('暂无可邀请对象')}
                />
            )}
        </div>
    );
});
